import {initializeApp} from "firebase/app";
import {getFirestore, connectFirestoreEmulator} from "firebase/firestore";
import {connectStorageEmulator, getStorage} from "firebase/storage";
import {connectAuthEmulator, getAuth} from "firebase/auth";
import {connectFunctionsEmulator, getFunctions, httpsCallable} from "firebase/functions";
import {firebaseConfig} from "../config/firebaseConfig.js";

export const fbApp = initializeApp(firebaseConfig);
export const fbDb = getFirestore(fbApp);
export const fbStorage = getStorage(fbApp);
export const fbAuth = getAuth(fbApp);
export const fbFunctions = getFunctions();

// Connect to Emulators.
// Detected from the actual hostname rather than PUBLIC_URL, which is now empty
// so that builds are hostname-agnostic and can be served on multiple domains.
const isLocalhost = ["127.0.0.1", "localhost", "[::1]"].includes(window.location.hostname);
if (isLocalhost) {
  connectAuthEmulator(getAuth(), "http://127.0.0.1:9099", { disableWarnings: false });
  connectFunctionsEmulator(fbFunctions, "127.0.0.1", 5004);
  connectStorageEmulator(fbStorage, "127.0.0.1", 9199);
  connectFirestoreEmulator(fbDb, '127.0.0.1', 8080);
}

export const fbSignIn = httpsCallable(fbFunctions, 'signIn');
export const fbSetupUser = httpsCallable(fbFunctions, 'setupUser');
export const fbSyncContactToUser = httpsCallable(fbFunctions, 'syncContactToUser');
export const fbGetLoggedInUser = httpsCallable(fbFunctions, 'getLoggedInUser');
export const fbGetUserRole = httpsCallable(fbFunctions, 'getUserRole');
export const fbUserIsAffiliate = httpsCallable(fbFunctions, 'userIsAffiliate');
export const fbUserIsAdmin = httpsCallable(fbFunctions, 'userIsAdmin');
export const fbUserIsPhysician = httpsCallable(fbFunctions, 'userIsPhysician');
export const fbExpireSession = httpsCallable(fbFunctions, 'expireSession');
export const fbLog = httpsCallable(fbFunctions, 'log');
export const fbSync = httpsCallable(fbFunctions, 'syncHubspotContactsToFirebase');
export const fbSyncCompanies = httpsCallable(fbFunctions, 'syncCompaniesToFirebase');
export const fbSeedCompanyVisitTypeFlags = httpsCallable(fbFunctions, 'seedCompanyVisitTypeFlags');
export const fbAreThereActiveStaffUsers = httpsCallable(fbFunctions, 'areThereActiveStaffUsers');
export const fbFixDateFieldsInVisit = httpsCallable(fbFunctions, 'fixDateFieldsInVisit');
export const fbFixUserReadDateFieldsInChatMessages = httpsCallable(fbFunctions, 'fixUserReadDateFieldsInChatMessages');
export const fbFixDateFieldsInChatMessages = httpsCallable(fbFunctions, 'fixDateFieldsInChatMessages');
export const fbTransformNotificationMessages = httpsCallable(fbFunctions, 'transformNotificationMessages');
export const fbTransformChatMessages = httpsCallable(fbFunctions, 'transformChatMessages');
export const fbTransformVisitMessages = httpsCallable(fbFunctions, 'transformVisitMessages');
export const fbGetAccuroContacts = httpsCallable(fbFunctions, 'getAccuroContacts');
export const fbGetAccuroPatients = httpsCallable(fbFunctions, 'getAccuroPatients');
export const fbCreateAccuroPatient = httpsCallable(fbFunctions, 'createAccuroPatient');
export const fbGetAccuroPatientAlert = httpsCallable(fbFunctions, 'getAccuroPatientAlert');
export const fbGetPatientPrescriptionHistory = httpsCallable(fbFunctions, 'getPatientPrescriptionHistory');
export const fbGetRecentPatientPharmacies = httpsCallable(fbFunctions, 'getRecentPatientPharmacies');
export const fbSendRx = httpsCallable(fbFunctions, 'sendRx', { timeout: 570000 });
export const fbCancelRx = httpsCallable(fbFunctions, 'cancelRx');
export const fbDeleteDraftPrescription = httpsCallable(fbFunctions, 'deleteDraftPrescription');
export const fbLogRxWriterEvent = httpsCallable(fbFunctions, 'logRxWriterEvent');
export const fbSendFax = httpsCallable(fbFunctions, 'sendFax');
export const fbCreateAccuroAppointment = httpsCallable(fbFunctions, 'createAccuroAppointment');
export const fbUpdateAccuroAppointmentType = httpsCallable(fbFunctions, 'updateAccuroAppointmentType');
export const fbGetLastThreeLabResults= httpsCallable(fbFunctions, 'getLastThreeLabResults');
export const fbProcessAccuroPatients = httpsCallable(fbFunctions, 'processAccuroPatients');
export const fbProcessAccuroAppointments = httpsCallable(fbFunctions, 'processAccuroAppointments');
export const fbProcessAccuroAppointmentUpdates = httpsCallable(fbFunctions, 'processAccuroAppointmentUpdates');
export const fbProcessAccuroLabGroups = httpsCallable(fbFunctions, 'processAccuroLabGroups');
export const fbProcessAccuroPrescriptions = httpsCallable(fbFunctions, 'processAccuroPrescriptions');
export const fbGetRxQueueStats = httpsCallable(fbFunctions, 'getRxQueueStats');
export const fbProcessAccuroIntakeDocuments = httpsCallable(fbFunctions, 'processAccuroIntakeDocuments');
export const fbExtractIntakePageText = httpsCallable(fbFunctions, 'extractIntakePageText', { timeout: 120000 });
export const fbProcessOfflineFaxRequests = httpsCallable(fbFunctions, 'processOfflineFaxRequests');
export const fbGetBtnxLabResults = httpsCallable(fbFunctions, 'getBtnxLabResults', { timeout: 130000 });
export const fbGetAllLabResultsForPatient = httpsCallable(fbFunctions, 'getAllLabResultsForPatient');
export const fbSearchVisits = httpsCallable(fbFunctions, 'searchVisits');
export const fbGetPastVisits = httpsCallable(fbFunctions, 'getPastVisits');
export const fbGetPrescriptions = httpsCallable(fbFunctions, 'getPrescriptions');
export const fbSearchPatients = httpsCallable(fbFunctions, 'searchPatients');
export const fbSearchEnrollmentPatients = httpsCallable(fbFunctions, 'searchEnrollmentPatients');
export const fbMergeAccuroPatients = httpsCallable(fbFunctions, 'mergeAccuroPatients');
export const fbUpdateAccuroPatientDemographics = httpsCallable(fbFunctions, 'updateAccuroPatientDemographics');
export const fbProcessBillingCsv = httpsCallable(fbFunctions, 'processBillingCsv', { timeout: 300000 });
